import { sqliteStorage } from './sqliteStorage';

// Local storage keys
const STORAGE_KEYS = {
  USER_SETTINGS: 'rebuildme_settings',
  ENTRIES: 'rebuildme_entries',
  DRAFT: 'rebuildme_draft',
  PASSCODE: 'rebuildme_passcode',
  PASSCODE_ENABLED: 'rebuildme_passcode_enabled',
  NOTIFICATION_ENABLED: 'rebuildme_notification_enabled',
  NOTIFICATION_TIME: 'rebuildme_notification_time',
  COLOR_THEME: 'rebuildme_color_theme',
  TEXT_SCALE: 'rebuildme_text_scale',
  AI_COMPANION_ENABLED: 'rebuildme_ai_companion_enabled',
  AI_WELCOME_CACHE: 'rebuildme_ai_welcome_cache',
  SECURITY_TYPE: 'rebuildme_security_type',
};

// Use a synchronous cache adapter that mirrors native persistence to SQLite.
const localStorage = sqliteStorage;

// ============================================================
// UNIFIED SECURITY MANAGEMENT (Mutually Exclusive)
// ============================================================
export const SECURITY_TYPE = {
  NONE: 'none',
  PASSCODE: 'passcode',
  BIOMETRIC: 'biometric'
};

export const getSecurityType = () => {
  const type = localStorage.getItem(STORAGE_KEYS.SECURITY_TYPE);
  console.log('🔒 getSecurityType:', type || SECURITY_TYPE.NONE);
  return type || SECURITY_TYPE.NONE;
};

export const setSecurityType = (type) => {
  console.log('🔒 setSecurityType:', type);
  localStorage.setItem(STORAGE_KEYS.SECURITY_TYPE, type);
};

// ============================================================
// DEFAULT PROMPTS
// ============================================================
const DEFAULT_PROMPTS = [
  { id: 'prompt_1', answerKey: 'answer1', question: 'One thing I did well today', defaultQuestion: 'One thing I did well today', isDefault: true, enabled: true, order: 0 },
  { id: 'prompt_2', answerKey: 'answer2', question: 'One thing I feel grateful for today', defaultQuestion: 'One thing I feel grateful for today', isDefault: true, enabled: true, order: 1 },
  { id: 'prompt_3', answerKey: 'answer3', question: 'One thing I have taken pride in today', defaultQuestion: 'One thing I have taken pride in today', isDefault: true, enabled: true, order: 2 },
  { id: 'prompt_4', answerKey: 'answer4', question: 'What would I say to a friend in my position?', defaultQuestion: 'What would I say to a friend in my position?', isDefault: true, enabled: true, order: 3 },
  { id: 'prompt_5', answerKey: 'answer5', question: 'Daily notes', defaultQuestion: 'Daily notes', isDefault: true, enabled: true, order: 4 },
];

export const getDefaultPrompts = () => DEFAULT_PROMPTS.map(p => ({ ...p }));

// ============================================================
// KEYWORD PLACEHOLDER MAP
// ============================================================
const KEYWORD_PLACEHOLDERS = {
  sleep: 'e.g., 7.5 hours',
  weight: 'e.g., 70 kg',
  steps: 'e.g., 8,000 steps',
  water: 'e.g., 8 glasses',
  mood: 'e.g., 7/10',
  calories: 'e.g., 2,000 cal',
  pages: 'e.g., 20 pages',
  minutes: 'e.g., 30 minutes',
  exercise: 'e.g., morning run',
  meditation: 'e.g., 10 minutes',
  reading: 'e.g., 2 chapters',
  walk: 'e.g., 20 minutes',
  run: 'e.g., 3 km',
  journal: 'e.g., one paragraph',
  food: 'e.g., healthy meals today',
  drink: 'e.g., 2 litres',
  prayer: 'e.g., morning and evening',
};

export const getPlaceholder = (prompt) => {
  if (!prompt) return '';
  const q = prompt.question.toLowerCase();

  if (q === 'daily notes') return 'Any thoughts for today...';

  for (const [keyword, placeholder] of Object.entries(KEYWORD_PLACEHOLDERS)) {
    if (q.includes(keyword)) return placeholder;
  }

  if (prompt.isDefault && prompt.question === prompt.defaultQuestion) {
    return 'Take a moment to reflect...';
  }

  return '';
};

// ============================================================
// AI Companion settings
// ============================================================
export const isAICompanionEnabled = () => {
  const value = localStorage.getItem(STORAGE_KEYS.AI_COMPANION_ENABLED);
  return value === null ? true : value === 'true';
};

export const setAICompanionEnabled = (enabled) => {
  localStorage.setItem(STORAGE_KEYS.AI_COMPANION_ENABLED, enabled.toString());
};

export const getAIWelcomeCache = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.AI_WELCOME_CACHE);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data.date === formatDate(new Date())) return data.message;
    return null;
  } catch { return null; }
};

export const saveAIWelcomeCache = (message) => {
  localStorage.setItem(STORAGE_KEYS.AI_WELCOME_CACHE, JSON.stringify({
    date: formatDate(new Date()),
    message,
  }));
};

export const clearAIWelcomeCache = () => {
  localStorage.removeItem(STORAGE_KEYS.AI_WELCOME_CACHE);
};

// ============================================================
// Color themes
// ============================================================
export const COLOR_THEMES = {
  pure: {
    name: 'Pure',
    primary: '#F5F5F5',
    secondary: '#E8E8E8',
    accent: '#D9D9D9',
    light: '#FFFFFF',
    textPrimary: '#333333',
    textSecondary: '#666666',
    logoText: '#333333',
  },
  sunrise: {
    name: 'Sunrise',
    primary: '#FFDA03',
    secondary: '#FFF7CC',
    accent: '#FFDA03',
    light: '#FFFDF0',
    textPrimary: '#5C3D00',
    textSecondary: '#7A5518',
    logoText: '#5C3D00',
  },
  sunset: {
    name: 'Sunset',
    primary: '#CC5500',
    secondary: '#2A2A2A',
    accent: '#CC5500',
    light: '#0D0D0D',
    textPrimary: '#FFE6CC',
    textSecondary: '#CCAA88',
    logoText: '#FFE6CC',
  },
  wisteria: {
    name: 'Wisteria',
    primary: '#D4C7EB',
    secondary: '#E9E3F5',
    accent: '#8E6ECC',
    light: '#F4F1FA',
    textPrimary: '#2D2D35',
    textSecondary: '#73737D',
    logoText: '#F9F9FF',
  },
  skyblue: {
    name: 'Sky Blue',
    primary: '#B8D4E8',
    secondary: '#D6E8F5',
    accent: '#5BA3D9',
    light: '#EEF6FB',
    textPrimary: '#2D2D35',
    textSecondary: '#73737D',
    logoText: '#F9F9FF',
  },
  sage: {
    name: 'Sage Green',
    primary: '#C5D9C5',
    secondary: '#DFE9DF',
    accent: '#7BA37B',
    light: '#F0F5F0',
    textPrimary: '#2D2D35',
    textSecondary: '#73737D',
    logoText: '#F9F9FF',
  },
  aubergine: {
    name: 'Aubergine',
    primary: '#5E3B6B',
    secondary: '#3F284A',
    accent: '#C084D4',
    light: '#1E1624',
    textPrimary: '#F3EAF8',
    textSecondary: '#CBB7D7',
    logoText: '#F9F3FF',
  },
  midnightblue: {
    name: 'Midnight Blue',
    primary: '#2F5C8F',
    secondary: '#1C3552',
    accent: '#68B5FF',
    light: '#0E1A2A',
    textPrimary: '#EAF4FF',
    textSecondary: '#B5C9DF',
    logoText: '#F4FAFF',
  },
  forestgreen: {
    name: 'Forest Green',
    primary: '#3F7A55',
    secondary: '#264A34',
    accent: '#7FD89A',
    light: '#0F1F15',
    textPrimary: '#E8F7EE',
    textSecondary: '#B2CEBC',
    logoText: '#F2FFF5',
  },
};

export const getColorTheme = () => {
  const savedTheme = localStorage.getItem(STORAGE_KEYS.COLOR_THEME);
  if (!savedTheme) return 'midnightblue';

  // If the saved theme key is no longer available on this branch/version,
  // fall back to a known-safe theme so the app can still render.
  if (!COLOR_THEMES[savedTheme]) {
    localStorage.setItem(STORAGE_KEYS.COLOR_THEME, 'midnightblue');
    return 'midnightblue';
  }

  return savedTheme;
};

export const saveColorTheme = (theme) => {
  localStorage.setItem(STORAGE_KEYS.COLOR_THEME, theme);
};

// ============================================================
// Text scaling
// ============================================================
export const TEXT_SCALES = {
  small: 0.93,
  default: 1,
  large: 1.08,
  xlarge: 1.16,
};

export const getTextScale = () => {
  const saved = localStorage.getItem(STORAGE_KEYS.TEXT_SCALE) || 'default';
  return TEXT_SCALES[saved] ? saved : 'default';
};

export const getTextScaleValue = (scaleKey = 'default') => {
  return TEXT_SCALES[scaleKey] || TEXT_SCALES.default;
};

export const applyTextScale = (scaleKey = 'default') => {
  const value = getTextScaleValue(scaleKey);
  document.documentElement.style.setProperty('--app-text-scale', String(value));
};

export const saveTextScale = (scaleKey) => {
  const safeKey = TEXT_SCALES[scaleKey] ? scaleKey : 'default';
  localStorage.setItem(STORAGE_KEYS.TEXT_SCALE, safeKey);
  applyTextScale(safeKey);
};

// ============================================================
// MIGRATION
// ============================================================
let _migrationIdCounter = 0;
const generateId = (prefix) => `${prefix}_${Date.now()}_${_migrationIdCounter++}`;

const migrateSettings = (old) => {
  if (!old || old.migrated) return old;

  const goals = (old.activities || []).map((act, i) => {
    const text = act.timeAmount && act.timeAmount.trim()
      ? `${act.timeAmount.trim()} ${act.name.trim()}`
      : act.name.trim();
    return {
      id: `goal_${i + 1}`,
      text,
      easyText: (act.easyActivity || act.failsafe || '').trim(),
      enabled: true,
      order: i,
    };
  });

  return {
    name: old.name || '',
    goals,
    prompts: getDefaultPrompts(),
    migrated: true,
  };
};

const migrateEntry = (entry, settings) => {
  if (!entry || entry.migrated) return entry;

  const goals = {};
  const goalList = settings?.goals || [];
  if (entry.activities) {
    entry.activities.forEach((act, i) => {
      if (goalList[i]) {
        goals[goalList[i].id] = {
          completed: !!act.completed,
          easyActivity: !!(act.easyActivity || act.failsafe),
        };
      }
    });
  }

  return {
    goals,
    answer1: entry.didWell || '',
    answer2: entry.grateful || '',
    answer3: entry.pride || '',
    answer4: entry.friend || '',
    answer5: entry.notes || '',
    aiResponse: entry.aiResponse || '',
    aiResponseAt: entry.aiResponseAt || '',
    savedAt: entry.savedAt || '',
    migrated: true,
  };
};

const ensureMigration = () => {
  const raw = localStorage.getItem(STORAGE_KEYS.USER_SETTINGS);
  if (!raw) return;
  const settings = JSON.parse(raw);
  if (settings.migrated) return;

  const newSettings = migrateSettings(settings);
  localStorage.setItem(STORAGE_KEYS.USER_SETTINGS, JSON.stringify(newSettings));

  const entriesRaw = localStorage.getItem(STORAGE_KEYS.ENTRIES);
  if (entriesRaw) {
    const entries = JSON.parse(entriesRaw);
    const migrated = {};
    for (const [date, entry] of Object.entries(entries)) {
      migrated[date] = migrateEntry(entry, newSettings);
    }
    localStorage.setItem(STORAGE_KEYS.ENTRIES, JSON.stringify(migrated));
  }

  const draftsRaw = localStorage.getItem(STORAGE_KEYS.DRAFT);
  if (draftsRaw) {
    const drafts = JSON.parse(draftsRaw);
    const migrated = {};
    for (const [date, draft] of Object.entries(drafts)) {
      migrated[date] = migrateEntry(draft, newSettings);
    }
    localStorage.setItem(STORAGE_KEYS.DRAFT, JSON.stringify(migrated));
  }
};

ensureMigration();

// ============================================================
// Settings management
// ============================================================
export const getSettings = () => {
  const settings = localStorage.getItem(STORAGE_KEYS.USER_SETTINGS);
  return settings ? JSON.parse(settings) : null;
};

export const saveSettings = (settings) => {
  localStorage.setItem(STORAGE_KEYS.USER_SETTINGS, JSON.stringify(settings));
};

export const hasCompletedOnboarding = () => {
  const settings = getSettings();
  return settings && settings.name && settings.migrated;
};

export const getEnabledGoals = () => {
  const settings = getSettings();
  if (!settings?.goals) return [];
  return settings.goals
    .filter(g => g.enabled)
    .sort((a, b) => a.order - b.order);
};

export const getEnabledPrompts = () => {
  const settings = getSettings();
  if (!settings?.prompts) return getDefaultPrompts();
  return settings.prompts
    .filter(p => p.enabled)
    .sort((a, b) => a.order - b.order);
};

export const addGoal = (goal) => {
  const settings = getSettings();
  if (!settings) return;
  const goals = settings.goals || [];
  if (goals.length >= 5) return;
  goals.push({
    id: generateId('goal'),
    text: goal.text,
    easyText: goal.easyText || '',
    enabled: true,
    order: goals.length,
  });
  saveSettings({ ...settings, goals });
};

export const updateGoal = (goalId, updates) => {
  const settings = getSettings();
  if (!settings) return;
  settings.goals = (settings.goals || []).map(g =>
    g.id === goalId ? { ...g, ...updates } : g
  );
  saveSettings(settings);
};

export const deleteGoal = (goalId) => {
  const settings = getSettings();
  if (!settings) return;
  settings.goals = (settings.goals || []).filter(g => g.id !== goalId);
  settings.goals.forEach((g, i) => { g.order = i; });
  saveSettings(settings);
};

export const addPrompt = (question) => {
  const settings = getSettings();
  if (!settings) return;
  const prompts = settings.prompts || getDefaultPrompts();
  if (prompts.length >= 10) return;
  const usedKeys = new Set(prompts.map(p => p.answerKey));
  let answerKey = null;
  for (let i = 1; i <= 10; i++) {
    if (!usedKeys.has(`answer${i}`)) {
      answerKey = `answer${i}`;
      break;
    }
  }
  if (!answerKey) return;
  prompts.push({
    id: generateId('prompt'),
    answerKey,
    question,
    defaultQuestion: null,
    isDefault: false,
    enabled: true,
    order: prompts.length,
  });
  saveSettings({ ...settings, prompts });
};

export const updatePrompt = (promptId, updates) => {
  const settings = getSettings();
  if (!settings) return;
  settings.prompts = (settings.prompts || []).map(p =>
    p.id === promptId ? { ...p, ...updates } : p
  );
  saveSettings(settings);
};

export const deletePrompt = (promptId) => {
  const settings = getSettings();
  if (!settings) return;
  const prompt = settings.prompts.find(p => p.id === promptId);
  if (!prompt || prompt.isDefault) return;
  settings.prompts = settings.prompts.filter(p => p.id !== promptId);
  settings.prompts.forEach((p, i) => { p.order = i; });
  saveSettings(settings);
};

export const resetPromptToDefault = (promptId) => {
  const settings = getSettings();
  if (!settings) return;
  settings.prompts = (settings.prompts || []).map(p => {
    if (p.id === promptId && p.isDefault && p.defaultQuestion) {
      return { ...p, question: p.defaultQuestion };
    }
    return p;
  });
  saveSettings(settings);
};

export const restoreAllDefaultPrompts = () => {
  const settings = getSettings();
  if (!settings) return;
  const defaults = getDefaultPrompts();
  const customPrompts = (settings.prompts || []).filter(p => !p.isDefault);
  const restoredDefaults = defaults.map(d => {
    const existing = (settings.prompts || []).find(p => p.id === d.id);
    return existing ? { ...existing, question: d.defaultQuestion, enabled: true } : d;
  });
  settings.prompts = [...restoredDefaults, ...customPrompts];
  settings.prompts.forEach((p, i) => { p.order = i; });
  saveSettings(settings);
};

export const reorderPrompts = (newOrder) => {
  const settings = getSettings();
  if (!settings) return;
  settings.prompts = newOrder.map((p, i) => ({ ...p, order: i }));
  saveSettings(settings);
};

export const reorderGoals = (newOrder) => {
  const settings = getSettings();
  if (!settings) return;
  settings.goals = newOrder.map((g, i) => ({ ...g, order: i }));
  saveSettings(settings);
};

// ============================================================
// Passcode management (with mutual exclusivity)
// ============================================================
export const getPasscode = () => {
  const passcode = localStorage.getItem(STORAGE_KEYS.PASSCODE);
  console.log('🔑 getPasscode:', passcode ? 'present' : 'null');
  return passcode;
};

export const savePasscode = (passcode) => {
  console.log('🔑 savePasscode');
  localStorage.setItem(STORAGE_KEYS.PASSCODE, passcode);
  setPasscodeEnabled(true);
};

export const clearPasscode = () => {
  console.log('🔑 clearPasscode');
  localStorage.removeItem(STORAGE_KEYS.PASSCODE);
  localStorage.removeItem(STORAGE_KEYS.PASSCODE_ENABLED);
  const currentType = getSecurityType();
  if (currentType === SECURITY_TYPE.PASSCODE) {
    setSecurityType(SECURITY_TYPE.NONE);
  }
};

export const isPasscodeEnabled = () => {
  const securityType = getSecurityType();
  const enabled = securityType === SECURITY_TYPE.PASSCODE;
  console.log('🔑 isPasscodeEnabled:', enabled);
  return enabled;
};

export const setPasscodeEnabled = (enabled) => {
  console.log('🔑 setPasscodeEnabled:', enabled);
  if (enabled) {
    setSecurityType(SECURITY_TYPE.PASSCODE);
    localStorage.setItem('rebuildme_biometrics_enabled', 'false');
  } else {
    const currentType = getSecurityType();
    if (currentType === SECURITY_TYPE.PASSCODE) {
      setSecurityType(SECURITY_TYPE.NONE);
    }
  }
};

// ============================================================
// Biometrics management (with mutual exclusivity)
// ============================================================
export const isBiometricsEnabled = () => {
  const securityType = getSecurityType();
  const enabled = securityType === SECURITY_TYPE.BIOMETRIC;
  console.log('📱 isBiometricsEnabled:', enabled);
  return enabled;
};

export const setBiometricsEnabled = (enabled) => {
  console.log('📱 setBiometricsEnabled:', enabled);
  if (enabled) {
    setSecurityType(SECURITY_TYPE.BIOMETRIC);
    localStorage.removeItem(STORAGE_KEYS.PASSCODE);
    localStorage.removeItem(STORAGE_KEYS.PASSCODE_ENABLED);
  } else {
    const currentType = getSecurityType();
    if (currentType === SECURITY_TYPE.BIOMETRIC) {
      setSecurityType(SECURITY_TYPE.NONE);
    }
  }
};

// ============================================================
// Entries management (WITH PROMPT SNAPSHOTS)
// ============================================================
export const getEntries = () => {
  const entries = localStorage.getItem(STORAGE_KEYS.ENTRIES);
  return entries ? JSON.parse(entries) : {};
};

export const saveEntry = (date, entryData) => {
  const entries = getEntries();
  const existingEntry = entries[date] || {};
  const settings = getSettings();
  const goals = settings?.goals || [];
  const prompts = settings?.prompts || getDefaultPrompts();

  const hasExistingGoalSnapshots = Array.isArray(existingEntry.goalSnapshots) && existingEntry.goalSnapshots.length > 0;
  const hasExistingPromptSnapshots = Array.isArray(existingEntry.promptSnapshots) && existingEntry.promptSnapshots.length > 0;

  // Preserve historical snapshots if they already exist.
  if (entryData.goalSnapshots) {
    // Caller provided explicit snapshots (e.g. today's entry with updated settings).
  } else if (hasExistingGoalSnapshots) {
    entryData.goalSnapshots = existingEntry.goalSnapshots;
  } else if (!entryData.goalSnapshots) {
    // Snapshot goals at the time of first save when no snapshot is supplied.
    entryData.goalSnapshots = goals.map(g => ({
      id: g.id,
      text: g.text,
      easyText: g.easyText,
      order: g.order,
    }));
  }

  if (entryData.promptSnapshots) {
    // Caller provided explicit snapshots (e.g. today's entry with updated settings).
  } else if (hasExistingPromptSnapshots) {
    entryData.promptSnapshots = existingEntry.promptSnapshots;
  } else if (!entryData.promptSnapshots) {
    // Snapshot prompts at the time of first save when no snapshot is supplied.
    entryData.promptSnapshots = prompts.map(p => ({
      id: p.id,
      question: p.question,
      answerKey: p.answerKey,
      order: p.order,
    }));
  }
  
  entryData.savedAt = new Date().toISOString();

  // Preserve AI text unless this save explicitly provides a replacement.
  if (entryData.aiResponse === undefined && existingEntry.aiResponse) {
    entryData.aiResponse = existingEntry.aiResponse;
  }
  if (entryData.aiResponseAt === undefined && existingEntry.aiResponseAt) {
    entryData.aiResponseAt = existingEntry.aiResponseAt;
  }
  
  entries[date] = { ...existingEntry, ...entryData };
  localStorage.setItem(STORAGE_KEYS.ENTRIES, JSON.stringify(entries));
  clearDraft(date);
};

export const updateEntryWithAIResponse = (date, aiResponse) => {
  const entries = getEntries();
  if (entries[date]) {
    entries[date].aiResponse = aiResponse;
    entries[date].aiResponseAt = new Date().toISOString();
    localStorage.setItem(STORAGE_KEYS.ENTRIES, JSON.stringify(entries));
  }
};

export const getEntry = (date) => {
  const entries = getEntries();
  return entries[date] || null;  // ✅ Return as-is, no transformation
};

export const deleteEntry = (date) => {
  const entries = getEntries();
  delete entries[date];
  localStorage.setItem(STORAGE_KEYS.ENTRIES, JSON.stringify(entries));
};

export const getDraft = (date) => {
  const drafts = localStorage.getItem(STORAGE_KEYS.DRAFT);
  const parsed = drafts ? JSON.parse(drafts) : {};
  return parsed[date] || null;
};

export const saveDraft = (date, draft) => {
  const drafts = localStorage.getItem(STORAGE_KEYS.DRAFT);
  const parsed = drafts ? JSON.parse(drafts) : {};
  parsed[date] = draft;
  localStorage.setItem(STORAGE_KEYS.DRAFT, JSON.stringify(parsed));
};

export const clearDraft = (date) => {
  const drafts = localStorage.getItem(STORAGE_KEYS.DRAFT);
  const parsed = drafts ? JSON.parse(drafts) : {};
  delete parsed[date];
  localStorage.setItem(STORAGE_KEYS.DRAFT, JSON.stringify(parsed));
};

export const addCommentToEntry = (date, commentText) => {
  const entries = getEntries();
  const entry = entries[date];
  if (!entry) return;
  
  if (!entry.comments) {
    entry.comments = [];
  }
  
  entry.comments.push({
    text: commentText.trim(),
    date: formatDate(new Date()),
    timestamp: new Date().toISOString(),
  });
  
  localStorage.setItem(STORAGE_KEYS.ENTRIES, JSON.stringify(entries));
};

// ============================================================
// Notification settings
// ============================================================
export const getNotificationSettings = () => ({
  enabled: localStorage.getItem(STORAGE_KEYS.NOTIFICATION_ENABLED) === 'true',
  time: localStorage.getItem(STORAGE_KEYS.NOTIFICATION_TIME) || '19:00',
});

export const saveNotificationSettings = (enabled, time) => {
  localStorage.setItem(STORAGE_KEYS.NOTIFICATION_ENABLED, enabled.toString());
  localStorage.setItem(STORAGE_KEYS.NOTIFICATION_TIME, time);
};

// ============================================================
// Backup & Restore
// ============================================================
export const exportAllData = () => ({
  version: 2,
  exportedAt: new Date().toISOString(),
  settings: getSettings(),
  entries: getEntries(),
  passcodeEnabled: isPasscodeEnabled(),
  notificationSettings: getNotificationSettings(),
  colorTheme: getColorTheme(),
  textScale: getTextScale(),
  aiCompanionEnabled: isAICompanionEnabled(),
  securityType: getSecurityType(),
});

export const importAllData = (data) => {
  if (data.settings && !data.settings.migrated) {
    const migratedSettings = migrateSettings(data.settings);
    saveSettings(migratedSettings);
    if (data.entries) {
      const entries = {};
      for (const [date, entry] of Object.entries(data.entries)) {
        entries[date] = migrateEntry(entry, migratedSettings);
      }
      localStorage.setItem(STORAGE_KEYS.ENTRIES, JSON.stringify(entries));
    }
  } else {
    if (data.settings) saveSettings(data.settings);
    if (data.entries) {
      localStorage.setItem(STORAGE_KEYS.ENTRIES, JSON.stringify(data.entries));
    }
  }
  if (data.passcodeEnabled !== undefined) setPasscodeEnabled(data.passcodeEnabled);
  if (data.notificationSettings) {
    saveNotificationSettings(data.notificationSettings.enabled, data.notificationSettings.time);
  }
  if (data.colorTheme) saveColorTheme(data.colorTheme);
  if (data.textScale) saveTextScale(data.textScale);
  if (data.aiCompanionEnabled !== undefined) setAICompanionEnabled(data.aiCompanionEnabled);
  if (data.securityType) setSecurityType(data.securityType);
};

export const clearAllData = () => {
  Object.values(STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
};

// ============================================================
// Date helpers
// ============================================================
export const formatDate = (date) => date.toISOString().split('T')[0];

export const formatDisplayDate = (dateStr) => {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export const formatShortDate = (dateStr) => {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};